/**
 * Core cache hooks for data fetching with automatic caching
 * Provides cache-first data loading with fallback to network
 */

import { useCallback, useEffect, useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCacheStore } from '@/stores/cacheStore';
import { CacheKeys, UserContext } from '@/stores/cacheKeys';
import { TTL_CONFIG, CacheDataType } from '@/stores/cacheConfig';

interface UseCachedDataOptions {
  enabled?: boolean;
  ttl?: number;
  fallbackToCache?: boolean;
  backgroundRefresh?: boolean;
}

interface UseCachedDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  isFromCache: boolean;
  refetch: () => Promise<void>;
  invalidate: () => Promise<void>;
}

/**
 * Generic hook for cached data fetching
 */
export const useCachedData = <T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
  options: UseCachedDataOptions = {}
): UseCachedDataResult<T> => {
  const {
    enabled = true,
    ttl,
    fallbackToCache = true,
    backgroundRefresh = false
  } = options;

  const { user, isStaff, staffSession } = useAuth();
  const { get, set, delete: deleteCache, isInitialized } = useCacheStore();
  
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);

  // Create user context (memoized to prevent infinite loops)
  const userContext: UserContext | null = useMemo(() => {
    return user || staffSession ? {
      userId: user?.id || staffSession?.id || '',
      restaurantId: staffSession?.restaurant_id || '',
      isStaff: isStaff,
      allowedBranches: staffSession?.allowed_branches || []
    } : null;
  }, [user, isStaff, staffSession]);

  const fetchData = useCallback(async (bypassCache = false) => {
    if (!enabled || !userContext) {
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // Try cache first (unless bypassing)
      if (!bypassCache && isInitialized) {
        const cachedData = await get<T>(cacheKey);
        if (cachedData !== null) {
          setData(cachedData);
          setIsFromCache(true);
          setLoading(false);

          // Background refresh if enabled
          if (backgroundRefresh) {
            setTimeout(() => fetchData(true), 100);
          }
          return;
        }
      }

      // Fetch from network
      setIsFromCache(false);
      if (!bypassCache) {
        setLoading(true);
      }

      const networkData = await fetcher();
      
      // Cache the result
      if (networkData !== null && isInitialized) {
        await set(cacheKey, networkData, ttl);
      }
      
      setData(networkData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      
      // Fallback to cache on network error
      if (fallbackToCache && !bypassCache && isInitialized) {
        try {
          const cachedData = await get<T>(cacheKey);
          if (cachedData !== null) {
            setData(cachedData);
            setIsFromCache(true);
            console.warn('Using cached data due to network error:', errorMessage);
          }
        } catch (cacheError) {
          console.error('Cache fallback failed:', cacheError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, userContext, cacheKey, fetcher, ttl, fallbackToCache, backgroundRefresh, get, set, isInitialized]);

  const refetch = useCallback(() => fetchData(true), [fetchData]);

  const invalidate = useCallback(async () => {
    try {
      await deleteCache(cacheKey);
      setData(null);
      setIsFromCache(false);
    } catch (err) {
      console.error('Cache invalidation failed:', err);
    }
  }, [cacheKey, deleteCache]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    refetch,
    invalidate
  };
};

/**
 * Hook for cached branches data
 */
export const useCachedBranches = (options?: UseCachedDataOptions) => {
  const { user, isStaff, staffSession } = useAuth();
  
  const userContext: UserContext | null = user || staffSession ? {
    userId: user?.id || staffSession?.id || '',
    restaurantId: staffSession?.restaurant_id || '',
    isStaff: isStaff,
    allowedBranches: staffSession?.allowed_branches || []
  } : null;

  const cacheKey = userContext ? CacheKeys.branches(userContext) : '';

  const fetcher = useCallback(async () => {
    // Import the actual fetch function to avoid circular dependencies
    const { fetchUserBranches } = await import('@/utils/userDataFilter');
    return fetchUserBranches(userContext?.userId || '');
  }, [userContext?.userId]);

  return useCachedData(
    cacheKey,
    fetcher,
    {
      ttl: TTL_CONFIG.branches,
      ...options
    }
  );
};

/**
 * Hook for cached categories data
 */
export const useCachedCategories = (branchId?: string, options?: UseCachedDataOptions) => {
  const { user, isStaff, staffSession } = useAuth();
  
  const userContext: UserContext | null = user || staffSession ? {
    userId: user?.id || staffSession?.id || '',
    restaurantId: staffSession?.restaurant_id || '',
    isStaff: isStaff,
    allowedBranches: staffSession?.allowed_branches || []
  } : null;

  // Public access fallback: if no user context, use a simple key prefix
  const isPublic = !userContext;
  const cacheKey = userContext && branchId 
    ? CacheKeys.categories(userContext, branchId) 
    : (branchId ? `public:categories:${branchId}` : '');

  const fetcher = useCallback(async () => {
    if (!branchId) return [];
    
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('branch_id', branchId)
      .order('sort_order', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return data || [];
  }, [branchId]);

  // If public, bypass the secure cache store logic that requires userContext
  // We can use a simple SWR-like pattern or just rely on the fetcher if we don't want to implement a public cache store right now.
  // However, useCachedData expects userContext to be present for its internal logic.
  // So we should probably return a direct fetch result if public.
  
  const cachedResult = useCachedData(
    cacheKey,
    fetcher,
    {
      enabled: !!branchId && !isPublic, // Only enable secure cache if not public
      ttl: TTL_CONFIG.categories,
      ...options
    }
  );

  // Public fetch logic
  const [publicData, setPublicData] = useState<any[] | null>(null);
  const [publicLoading, setPublicLoading] = useState(false);

  useEffect(() => {
    if (isPublic && branchId) {
      setPublicLoading(true);
      fetcher()
        .then(data => setPublicData(data))
        .catch(err => console.error("Public category fetch error:", err))
        .finally(() => setPublicLoading(false));
    }
  }, [isPublic, branchId, fetcher]);

  if (isPublic) {
    return {
      data: publicData,
      loading: publicLoading,
      error: null,
      isFromCache: false,
      refetch: async () => {
        const data = await fetcher();
        setPublicData(data);
      },
      invalidate: async () => {}
    };
  }

  return cachedResult;
};

/**
 * Hook for cached menu items data
 */
export const useCachedMenuItems = (categoryId?: string, options?: UseCachedDataOptions) => {
  const { user, isStaff, staffSession } = useAuth();
  
  const userContext: UserContext | null = user || staffSession ? {
    userId: user?.id || staffSession?.id || '',
    restaurantId: staffSession?.restaurant_id || '',
    isStaff: isStaff,
    allowedBranches: staffSession?.allowed_branches || []
  } : null;

  const isPublic = !userContext;
  const cacheKey = userContext && categoryId 
    ? CacheKeys.menuItems(userContext, categoryId) 
    : (categoryId ? `public:menuItems:${categoryId}` : '');

  const fetcher = useCallback(async () => {
    if (!categoryId) return [];
    
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase
      .from('menu_items')
      .select('*')
      .eq('category_id', categoryId)
      .order('sort_order', { ascending: true, nullsFirst: false });

    if (error) throw error;
    return data || [];
  }, [categoryId]);

  const cachedResult = useCachedData(
    cacheKey,
    fetcher,
    {
      enabled: !!categoryId && !isPublic,
      ttl: TTL_CONFIG.menuItems,
      ...options
    }
  );

  // Public fetch logic
  const [publicData, setPublicData] = useState<any[] | null>(null);
  const [publicLoading, setPublicLoading] = useState(false);

  useEffect(() => {
    if (isPublic && categoryId) {
      setPublicLoading(true);
      fetcher()
        .then(data => setPublicData(data))
        .catch(err => console.error("Public menu item fetch error:", err))
        .finally(() => setPublicLoading(false));
    }
  }, [isPublic, categoryId, fetcher]);

  if (isPublic) {
    return {
      data: publicData,
      loading: publicLoading,
      error: null,
      isFromCache: false,
      refetch: async () => {
        const data = await fetcher();
        setPublicData(data);
      },
      invalidate: async () => {}
    };
  }

  return cachedResult;
};

/**
 * Hook for cached delivery zones data
 */
export const useCachedDeliveryZones = (branchId?: string, options?: UseCachedDataOptions) => {
  const { user, isStaff, staffSession } = useAuth();
  
  const userContext: UserContext | null = user || staffSession ? {
    userId: user?.id || staffSession?.id || '',
    restaurantId: staffSession?.restaurant_id || '',
    isStaff: isStaff,
    allowedBranches: staffSession?.allowed_branches || []
  } : null;

  const cacheKey = userContext && branchId ? CacheKeys.deliveryZones(userContext, branchId) : '';

  const fetcher = useCallback(async () => {
    if (!branchId) return [];
    
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase
      .from('delivery_zones')
      .select('*')
      .eq('branch_id', branchId)
      .order('delivery_fee', { ascending: true });

    if (error) throw error;
    return data || [];
  }, [branchId]);

  return useCachedData(
    cacheKey,
    fetcher,
    {
      enabled: !!branchId,
      ttl: TTL_CONFIG.deliveryZones,
      ...options
    }
  );
};

/**
 * Hook for cached orders data
 */
export const useCachedOrders = (branchId?: string, options?: UseCachedDataOptions) => {
  const { user, isStaff, staffSession } = useAuth();
  
  const userContext: UserContext | null = user || staffSession ? {
    userId: user?.id || staffSession?.id || '',
    restaurantId: staffSession?.restaurant_id || '',
    isStaff: isStaff,
    allowedBranches: staffSession?.allowed_branches || []
  } : null;

  const cacheKey = userContext && branchId ? CacheKeys.orders(userContext, branchId) : '';

  const fetcher = useCallback(async () => {
    if (!branchId) return [];
    
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase
      .from('orders')
      .select(`
        id,
        branch_id,
        customer_name,
        customer_phone,
        customer_email,
        order_number,
        status,
        total_amount,
        notes,
        created_at,
        updated_at,
        customer_address,
        order_type,
        table_number,
        payment_method,
        delivery_fee,
        estimated_delivery_time,
        service_amount,
        tax_amount,
        subtotal_amount,
        order_items (
          id,
          order_id,
          menu_item_id,
          quantity,
          unit_price,
          total_price,
          notes,
          is_acknowledged,
          is_removed,
          menu_items (
            name,
            description
          )
        )
      `)
      .eq('branch_id', branchId)
      .order('created_at', { ascending: false })
      .limit(100); // Added limit to prevent high egress

    if (error) throw error;
    return data || [];
  }, [branchId]);

  return useCachedData(
    cacheKey,
    fetcher,
    {
      enabled: !!branchId,
      ttl: TTL_CONFIG.orders,
      backgroundRefresh: true, // Keep orders fresh in background
      ...options
    }
  );
};

/**
 * Cache invalidation hook
 */
export const useCacheInvalidation = () => {
  const { invalidate } = useCacheStore();
  const { user, isStaff, staffSession } = useAuth();

  const invalidateCache = useCallback(async (
    dataTypes: CacheDataType[],
    branchId?: string
  ) => {
    if (!user && !staffSession) return;
    
    try {
      await invalidate(dataTypes, branchId);
    } catch (error) {
      console.error('Cache invalidation failed:', error);
    }
  }, [invalidate, user, staffSession]);

  return { invalidateCache };
};